<template>
  <d-card class="card-small mb-3">
    <d-card-header class="border-bottom">
      <h6 class="m-0">{{ $t('puzzle.card.publication') }}</h6>
    </d-card-header>

    <d-card-body class="px-0">
      <d-list-group flush>
        <d-list-group-item class="px-3 py-0">
          <div class="pb-1">
            {{ $t('puzzle.sidebar.createdAt') }} {{ model.f_created }}
          </div>
          <div class="pb-1">
            {{ $t('puzzle.sidebar.updatedAt') }} {{ model.f_updated }}
          </div>
        </d-list-group-item>
        <d-list-group-item class="p-3">
          <d-button
            v-if="model.id && !isReadOnly"
            @click="requestFinalize"
            class="btn-outline-success btn-block mb-2"
          >
            {{
              isPuzzleStatusCompleted(model.status)
                ? $t('puzzle.sidebar.setDraft')
                : $t('puzzle.sidebar.setCompleted')
            }}
          </d-button>
          <d-button
            :disabled="!canExport"
            @click.prevent="toggleExportModal()"
            class="btn-dark btn-block"
          >
            {{ $t('shared.actions.export') }}
          </d-button>
          <p
            v-if="!canExport"
            class="text-muted mb-1 small"
            style="color: #6c757d !important"
          >
            {{ $t('puzzle.sidebar.exportHint') }}
          </p>
          <d-button
            v-if="!isReadOnly"
            @click="openImportModal"
            class="btn-outline-light border btn-block"
          >
            <i class="material-icons">cloud_upload</i>
            {{ $t('puzzle.sidebar.importFile') }}
          </d-button>
          <d-button
            @click="openSaveTemplateModal"
            class="btn-outline-light border btn-block"
          >
            {{ $t('puzzle.sidebar.saveAsTemplate') }}
          </d-button>
          <d-button
            v-if="model.id && !isReadOnly"
            @click.prevent="requestSyncQuestions"
            class="btn-outline-light border btn-block"
            :disabled="isSyncingQuestions"
          >
            {{ $t('puzzle.sidebar.syncQuestions') }}
          </d-button>
          <d-button
            v-if="!isReadOnly"
            @click.prevent="generateNewFromCurrentTemplate()"
            class="btn-info btn-block mt-2"
            :disabled="isGenerating || !hasAnyLetter"
          >
            {{ $t('puzzle.sidebar.saveAndGenerate') }}
          </d-button>
          <p v-if="!hasAnyLetter" class="text-muted mb-0 small mb-2">
            {{ $t('puzzle.sidebar.emptyGridHint') }}
          </p>
          <d-button
            v-if="!isReadOnly"
            @click.prevent="fillCrossword()"
            class="btn-dark btn-block"
            :disabled="hasNoQuestions"
          >
            {{ $t('puzzle.sidebar.fillWords') }}
          </d-button>
          <p v-if="hasNoQuestions" class="text-muted mb-0 small">
            {{ $t('puzzle.sidebar.noQuestionsHint') }}
          </p>
        </d-list-group-item>

        <d-list-group-item
          class="p-3 d-flex justify-content-around"
          style="gap: 8px"
          v-if="model.id"
        >
          <d-button
            @click="navigateTo(model.prev_id)"
            class="btn-light border flex-fill"
            :disabled="!model.prev_id"
          >
            <i class="material-icons">navigate_before</i>
            {{ $t('shared.pagination.prev') }}
          </d-button>

          <d-button
            @click="navigateTo(model.next_id)"
            class="btn-light border flex-fill"
            :disabled="!model.next_id"
          >
            {{ $t('shared.pagination.next') }}
            <i class="material-icons">navigate_next</i>
          </d-button>
        </d-list-group-item>
      </d-list-group>
    </d-card-body>

    <d-card-footer
      class="border-top d-flex flex-column flex-xl-row justify-content-around"
    >
      <d-button
        @click="save"
        type="button"
        class="btn-primary mb-3 mb-xl-0 col-xl-5"
        :disabled="isReadOnly"
      >
        {{ $t('shared.actions.save') }}
      </d-button>
      <router-link
        class="btn-secondary btn text-white col-xl-5"
        :to="{ name: 'admin-crossword-list', params: { page: 1 } }"
      >
        {{ $t('shared.actions.cancel') }}
      </router-link>
    </d-card-footer>

    <export-file-modal
      gameType="crossword"
      :items="[model]"
      :fileName="model.name || 'crossword'"
    />

    <unsaved-changes-modal @confirm="confirmNavigation" />
  </d-card>
</template>

<script>
import { mapActions } from 'vuex';
import ExportFileModal from '_common/shared/ExportFileModal.vue';
import UnsavedChangesModal from './modal/UnsavedChangesModal.vue';
import { isPuzzleStatusCompleted, MODEL } from './PuzzleModel';
import puzzleReadOnly from '../mixins/puzzleReadOnly';

export default {
  name: 'sidebar-actions',
  mixins: [puzzleReadOnly],
  props: {
    model: {
      type: Object,
      default: () => MODEL,
    },
    isGenerating: {
      type: Boolean,
      default: false,
    },
    isSyncingQuestions: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    ExportFileModal,
    UnsavedChangesModal,
  },
  data() {
    return {
      pendingNavigationId: null,
      isPuzzleStatusCompleted,
    };
  },
  computed: {
    canExport() {
      const isPageCreation = !this.$route.params.puzzleId;

      if (isPageCreation) return false;
      if (this.hasChanges()) return false;

      return true;
    },
    hasNoQuestions() {
      return !this.model.table.some(cell => cell.isQuestion);
    },
    hasAnyLetter() {
      return this.model.table.some(
        cell => cell.text && cell.text.trim() !== '',
      );
    },
  },
  methods: {
    ...mapActions(['setToggle', 'setHasChanges']),
    requestFinalize() {
      this.$emit('requestFinalize');
    },
    requestSyncQuestions() {
      this.$emit('requestSyncQuestions');
    },
    goBack() {
      this.$router.go(-1);
    },
    generate() {
      this.$emit('generate', this.model);
    },
    fillCrossword() {
      this.$emit('fillCrossword');
    },
    generateNewFromCurrentTemplate() {
      this.$emit('generateNewFromCurrentTemplate');
    },
    generateBank() {
      this.$emit('generateBank', true);
    },
    clearBank() {
      this.$emit('clearBank');
    },
    save() {
      this.$emit('save', this.model);
    },
    toggleModal() {
      this.setToggle({ type: 'export_file_modal', bool: true });
    },
    openImportModal() {
      this.setToggle({ type: 'import_file_modal', bool: true });
    },
    toggleExportModal() {
      this.setToggle({ type: 'export_file_modal', bool: true });
    },
    openSaveTemplateModal() {
      this.setToggle({ type: 'save_template_modal', bool: true });
    },
    openUnsavedChangesModal() {
      this.setToggle({ type: 'unsaved_changes_modal', bool: true });
    },
    hasChanges() {
      return this.$store.state.puzzle.hasChanges;
    },
    navigateTo(id) {
      if (!this.hasChanges()) {
        this.go(id);
        return;
      }

      this.pendingNavigationId = id;
      this.openUnsavedChangesModal();
    },
    confirmNavigation() {
      this.setHasChanges(false);
      this.setToggle({ type: 'unsaved_changes_modal', bool: false });
      this.go(this.pendingNavigationId);
    },
    go(id) {
      this.$router.push({
        name: 'admin-puzzle-edit',
        params: {
          puzzleId: id,
        },
      });
    },
  },
};
</script>
